import type { GlobalSearchHit } from "../types";

// Nylige elementer (PR-S3 i søkeplanen): de siste besøkte postene lagres i
// localStorage og vises øverst i Cmd+K-paletten før man har skrevet noe,
// à la Asana. Kun ren lagringslogikk her – sporing og visning bor i
// CommandPalette.

export type RecentItem = {
  type: Exclude<GlobalSearchHit["entity_type"], "prospect">;
  id: string;
  title: string;
  subtitle?: string | null;
};

const STORAGE_KEY = "mimin.recentItems";
export const MAX_RECENT_ITEMS = 15;

const isRecentItem = (value: unknown): value is RecentItem => {
  const item = value as RecentItem;
  return (
    !!item &&
    typeof item === "object" &&
    typeof item.id === "string" &&
    typeof item.title === "string" &&
    ["company", "contact", "project", "work_item", "deal"].includes(item.type)
  );
};

// Robust lesing: ødelagt/fremmed innhold i localStorage gir tom liste.
export const readRecentItems = (
  storage: Pick<Storage, "getItem"> = localStorage,
): RecentItem[] => {
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isRecentItem).slice(0, MAX_RECENT_ITEMS);
  } catch {
    return [];
  }
};

// Legg en post øverst; samme post (type+id) flyttes opp i stedet for å
// dupliseres, og listen kappes ved MAX_RECENT_ITEMS.
export const pushRecentItem = (
  item: RecentItem,
  storage: Pick<Storage, "getItem" | "setItem"> = localStorage,
): RecentItem[] => {
  const next = [
    item,
    ...readRecentItems(storage).filter(
      (existing) => !(existing.type === item.type && existing.id === item.id),
    ),
  ].slice(0, MAX_RECENT_ITEMS);
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Full/utilgjengelig storage – nylige er ren komfort, ignorer.
  }
  return next;
};
